import { modLoaderOf } from './addons.js';
// CurseForge API の取得元。API キーが必要（fetchJson 側で x-api-key を付ける）。
// 作者が外部からの自動ダウンロードを許可していないファイル（downloadUrl が null）は入れられない。
import { z } from 'zod';
import type { AddonFetchJson } from './providers.js';
import {
  isPluginPlatform,
  ModFileSchema,
  type AddonProject,
  type AddonSource,
  type AddonVersion,
  type ModLoader,
  type ModSearchHit,
} from './mods.js';

export const CURSEFORGE_API = 'https://api.curseforge.com/v1';
const MINECRAFT = 432;
const CLASS_MODS = 6;
const CLASS_BUKKIT_PLUGINS = 5;
const loaderType: Partial<Record<ModLoader, number>> = { forge: 1, fabric: 4, neoforge: 6 };
const loaderName: Partial<Record<ModLoader, string>> = { forge: 'Forge', fabric: 'Fabric', neoforge: 'NeoForge' };

const modSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  name: z.string(),
  summary: z.string().default(''),
  logo: z.object({ thumbnailUrl: z.string().nullish() }).nullish(),
  downloadCount: z.number().default(0),
  categories: z.array(z.object({ name: z.string() })).default([]),
  classId: z.number().int().nullish(),
});

export const CurseForgeFileSchema = z.object({
  id: z.number().int(),
  modId: z.number().int(),
  displayName: z.string(),
  fileName: z.string(),
  releaseType: z.number().int(),
  fileDate: z.string(),
  downloadUrl: z.string().nullish(),
  hashes: z.array(z.object({ value: z.string(), algo: z.number().int() })).default([]),
  fileLength: z.number().int(),
  gameVersions: z.array(z.string()).default([]),
  dependencies: z.array(z.object({ modId: z.number().int(), relationType: z.number().int() })).default([]),
});
type RawFile = z.infer<typeof CurseForgeFileSchema>;

interface CurseForgeVersion extends AddonVersion {
  gameVersions: string[];
}

const relation: Record<number, AddonVersion['deps'][number]['type'] | undefined> = {
  1: 'embedded',
  2: 'optional',
  3: 'required',
  5: 'incompatible',
  6: 'embedded',
};

function modpackFile(raw: RawFile): Pick<CurseForgeVersion, 'file' | 'fileProblem'> {
  const sha1 = raw.hashes.find((h) => h.algo === 1)?.value.toLowerCase();
  if (!raw.downloadUrl)
    return {
      file: undefined,
      fileProblem:
        'この Modpack は作者が外部からの自動ダウンロードを許可していません。CurseForge から zip を落として、下の「アップロード」から入れてください',
    };
  if (!sha1 || !/^[0-9a-f]{40}$/.test(sha1) || !/^[^/\\]+\.zip$/i.test(raw.fileName))
    return { file: undefined, fileProblem: 'Modpack のファイル（zip とハッシュ）がありません' };
  return { file: { url: raw.downloadUrl, filename: raw.fileName, sha1, size: raw.fileLength }, fileProblem: undefined };
}

function normalize(raw: RawFile): CurseForgeVersion {
  const sha1 = raw.hashes.find((h) => h.algo === 1)?.value.toLowerCase();
  const file =
    raw.downloadUrl && sha1
      ? ModFileSchema.safeParse({ url: raw.downloadUrl, filename: raw.fileName, sha1, size: raw.fileLength })
      : undefined;
  return {
    id: String(raw.id),
    projectId: String(raw.modId),
    number: raw.displayName,
    type: raw.releaseType === 1 ? 'release' : raw.releaseType === 2 ? 'beta' : 'alpha',
    date: raw.fileDate,
    gameVersions: raw.gameVersions,
    deps: raw.dependencies.flatMap((d) => {
      const type = relation[d.relationType];
      return type ? [{ projectId: String(d.modId), type }] : [];
    }),
    file: file?.success ? file.data : undefined,
    fileProblem: raw.downloadUrl
      ? sha1
        ? undefined
        : 'ファイルのハッシュがありません'
      : 'CurseForge の作者が外部からの自動ダウンロードを許可していません（手動で入れてください）',
  };
}

export class CurseForgeClient implements AddonSource {
  readonly name = 'curseforge' as const;
  private readonly projects = new Map<string, AddonProject>();
  private readonly versions = new Map<string, CurseForgeVersion>();

  constructor(
    private readonly fetchJson: AddonFetchJson,
    private readonly base = CURSEFORGE_API,
  ) {}

  async getFiles(fileIds: number[]): Promise<RawFile[]> {
    const result: RawFile[] = [];
    const ids = z.array(z.number().int().positive()).parse([...new Set(fileIds)]);
    for (let offset = 0; offset < ids.length; offset += 500) {
      const response = await this.fetchJson(this.base + '/mods/files', {
        method: 'POST',
        body: JSON.stringify({ fileIds: ids.slice(offset, offset + 500) }),
      });
      result.push(...z.object({ data: z.array(CurseForgeFileSchema) }).parse(response).data);
    }
    return result;
  }
  async getMods(modIds: number[]): Promise<AddonProject[]> {
    const result: AddonProject[] = [];
    const ids = z.array(z.number().int().positive()).parse([...new Set(modIds)]);
    for (let offset = 0; offset < ids.length; offset += 500) {
      const response = await this.fetchJson(this.base + '/mods', {
        method: 'POST',
        body: JSON.stringify({ modIds: ids.slice(offset, offset + 500) }),
      });
      result.push(
        ...z
          .object({ data: z.array(modSchema) })
          .parse(response)
          .data.map((raw) => this.toProject(raw)),
      );
    }
    return result;
  }
  async packFile(projectId: string, fileId: string) {
    if (!/^\d+$/.test(projectId) || !/^\d+$/.test(fileId)) throw new Error('CurseForge ID が不正です');
    const raw = z
      .object({ data: CurseForgeFileSchema })
      .parse(await this.fetchJson(this.base + '/mods/' + projectId + '/files/' + fileId)).data;
    if (String(raw.modId) !== projectId || String(raw.id) !== fileId)
      throw new Error('CurseForge のファイル ID が一致しません');
    return raw;
  }
  async searchModpacks(query: string, offset = 0): Promise<ModSearchHit[]> {
    const params = new URLSearchParams({
      gameId: '432',
      classId: '4471',
      searchFilter: query,
      pageSize: '20',
      index: String(offset),
      sortField: '2',
      sortOrder: 'desc',
    });
    const result = z
      .object({ data: z.array(modSchema) })
      .parse(await this.fetchJson(this.base + '/mods/search?' + params));
    return result.data.map((raw) => ({
      source: 'curseforge',
      projectId: String(raw.id),
      slug: raw.slug,
      title: raw.name,
      description: raw.summary,
      iconUrl: raw.logo?.thumbnailUrl ?? undefined,
      downloads: raw.downloadCount,
      categories: raw.categories.map((c) => c.name),
      clientSide: 'unknown',
      serverSide: 'unknown',
    }));
  }
  async modpackVersions(projectId: string) {
    if (!/^\d+$/.test(projectId)) throw new Error('CurseForge ID が不正です');
    const project = z.object({ data: modSchema }).parse(await this.fetchJson(this.base + '/mods/' + projectId)).data;
    if (project.classId !== 4471) throw new Error('CurseForge の Modpack ではありません');
    const result = z
      .object({ data: z.array(CurseForgeFileSchema) })
      .parse(await this.fetchJson(this.base + '/mods/' + projectId + '/files?pageSize=50'));
    return result.data
      .map((raw) => ({
        ...normalize(raw),
        // normalize は Mod 用（.jar だけ）なので、Modpack の .zip はここで作る
        ...modpackFile(raw),
        loaders: raw.gameVersions.filter((v) => ['Fabric', 'Forge', 'NeoForge'].includes(v)),
      }))
      .sort(
        (a, b) =>
          ({ release: 0, beta: 1, alpha: 2 })[a.type] - { release: 0, beta: 1, alpha: 2 }[b.type] ||
          b.date.localeCompare(a.date),
      );
  }
  private classFor(loader: ModLoader) {
    return isPluginPlatform(loader) ? CLASS_BUKKIT_PLUGINS : CLASS_MODS;
  }

  private toProject(raw: z.infer<typeof modSchema>): AddonProject {
    // CurseForge はサーバー用かクライアント用かを持っていないので「不明」とする（両方に入れる扱い）
    return {
      kind:
        raw.classId === CLASS_MODS
          ? 'mod'
          : raw.classId === CLASS_BUKKIT_PLUGINS
            ? 'plugin'
            : raw.classId
              ? 'unsupported'
              : undefined,
      id: String(raw.id),
      slug: raw.slug,
      title: raw.name,
      description: raw.summary,
      iconUrl: raw.logo?.thumbnailUrl ?? undefined,
      client: 'unknown',
      server: 'unknown',
      classId: raw.classId ?? undefined,
    };
  }

  async search(query: string, loader: ModLoader, mcVersion: string, offset = 0): Promise<ModSearchHit[]> {
    const params = new URLSearchParams({
      gameId: String(MINECRAFT),
      classId: String(this.classFor(loader)),
      searchFilter: query,
      gameVersion: mcVersion,
      sortField: '2',
      sortOrder: 'desc',
      pageSize: '20',
      index: String(offset),
    });
    const type = loaderType[modLoaderOf(loader) ?? loader];
    if (type) params.set('modLoaderType', String(type));
    const result = z
      .object({ data: z.array(modSchema) })
      .parse(await this.fetchJson(`${this.base}/mods/search?${params}`));
    return result.data.map((raw) => ({
      source: 'curseforge',
      projectId: String(raw.id),
      slug: raw.slug,
      title: raw.name,
      description: raw.summary,
      iconUrl: raw.logo?.thumbnailUrl ?? undefined,
      downloads: raw.downloadCount,
      categories: raw.categories.map((c) => c.name),
      clientSide: 'unknown',
      serverSide: 'unknown',
    }));
  }

  async project(ref: string): Promise<AddonProject> {
    const cached = this.projects.get(ref);
    if (cached) return cached;
    let raw: z.infer<typeof modSchema> | undefined;
    if (/^\d+$/.test(ref)) {
      raw = z.object({ data: modSchema }).parse(await this.fetchJson(`${this.base}/mods/${ref}`)).data;
    } else {
      const params = new URLSearchParams({ gameId: String(MINECRAFT), slug: ref });
      raw = z.object({ data: z.array(modSchema) }).parse(await this.fetchJson(`${this.base}/mods/search?${params}`))
        .data[0];
    }
    if (!raw) throw new Error('not found');
    const project = this.toProject(raw);
    for (const key of [project.id, project.slug, ref]) this.projects.set(key, project);
    return project;
  }

  async version(id: string, projectId?: string): Promise<AddonVersion> {
    const cached = this.versions.get(id);
    if (cached) return cached;
    if (!projectId) throw new Error('CurseForge のファイルにはプロジェクト ID が必要です');
    const raw = z
      .object({ data: CurseForgeFileSchema })
      .parse(await this.fetchJson(`${this.base}/mods/${projectId}/files/${encodeURIComponent(id)}`)).data;
    const version = normalize(raw);
    this.versions.set(version.id, version);
    return version;
  }

  compatible(version: AddonVersion, loader: ModLoader, mcVersion: string): boolean {
    const versions = (version as CurseForgeVersion).gameVersions ?? [];
    if (!versions.includes(mcVersion)) return false;
    const name = loaderName[modLoaderOf(loader) ?? loader];
    return name ? versions.includes(name) : true;
  }

  async latestCompatible(projectId: string, loader: ModLoader, mcVersion: string): Promise<AddonVersion | undefined> {
    const params = new URLSearchParams({ gameVersion: mcVersion, pageSize: '50' });
    const type = loaderType[modLoaderOf(loader) ?? loader];
    if (type) params.set('modLoaderType', String(type));
    const list = z
      .object({ data: z.array(CurseForgeFileSchema) })
      .parse(await this.fetchJson(`${this.base}/mods/${projectId}/files?${params}`))
      .data.map(normalize);
    for (const version of list) this.versions.set(version.id, version);
    const rank = { release: 0, beta: 1, alpha: 2 } as const;
    return list
      .filter((v) => this.compatible(v, loader, mcVersion))
      .sort((a, b) => rank[a.type] - rank[b.type] || b.date.localeCompare(a.date))[0];
  }
}
